import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEOHead from "@/components/SEOHead";

/**
 * Catch-all 404. Wired as <Route path="*"> in AppRouter so any URL that
 * matches no other route (typos, removed pages, legacy paths not handled at
 * the edge) renders this instead of empty chrome. noindex,nofollow keeps
 * soft-404s out of the index (SEO_AUDIT_v2 PF-3). Legacy /news/* (301) and
 * /wp-content/* (410) are handled at the CloudFront edge, not here.
 */

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const POPULAR = [
  { label: "Pricing", to: "/pricing" },
  { label: "Contact us", to: "/contact" },
  { label: "See how much I'd save", to: "/switch-and-save" },
];

export default function NotFoundPage() {
  return (
    <div data-testid="not-found-page" data-measurement-page="not_found">
      <SEOHead
        title="Page Not Found"
        description="The page you were looking for doesn't exist or has moved. Explore CanDo Payments products, pricing and resources instead."
        path="/404"
        robots="noindex, nofollow"
        suppressBreadcrumb
      />

      {/* Hero */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p
              variants={fadeUp}
              className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3"
            >
              404
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-5"
            >
              Page <span className="gradient-text">not found</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed">
              The page you were looking for doesn&rsquo;t exist or may have moved.
              Let&rsquo;s get you back on track.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Navigation help */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors"
            data-testid="not-found-home-cta"
          >
            Back to home <ArrowRight size={14} />
          </Link>
          <p className="mt-8 text-sm font-semibold uppercase tracking-wider text-brand-grey">
            Popular destinations
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
            {POPULAR.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="inline-flex items-center gap-1.5 rounded-full border border-brand-light-grey px-4 py-2 text-sm font-semibold text-brand-black hover:border-brand-aqua hover:text-brand-aqua transition-colors"
              >
                {item.label}
                <ArrowRight size={13} />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
